import {
  ApiData,
  ApiDescription,
  MentorAdminComment,
  MentorData,
  RequestsMethod,
  StudentTier,
  User,
} from '@shared/clientTypes';
import { GetParentViewEnrollmentTimelineDTO } from '@shared/clientTypes/DTOs/Parents.DTO';
import { GetParentViewUniversitiesApplyingToDTO } from '@shared/clientTypes/DTOs/ParentsDTO';
import {
  DeleteAdmissionDocumentDTO,
  GetUserDetailsDTO,
  GetUsersByStatsMetricDTO,
  GetVideoSettingsDTO,
  SearchStudentsDTO,
  SearchStudentsForMeetingDTO,
  SearchUsersDTO,
  StatsByMetricDTO,
  WhatsAppGroupsDTO,
  UserCommentDTO,
  UserProfileDTO,
  DownloadStudentApplicationsCSVDTO,
  ExportRole4UsersCSVDTO,
} from '@shared/clientTypes/DTOs/UserDTO';
import { RoleType } from '@shared/serverTypes';
import {
  GoalStatusTypes,
  MyAccountInfo,
  PaginationAndSortingParams,
  PermissionsType,
  ProfileCompletionFieldsType,
  TriState,
  UserFieldParam,
  UserRole,
} from '../common';

export const isUserOrParentEmailRegistered: ApiDescription = {
  endpoint: '/users/is-user-or-parent-email-registered',
  method: RequestsMethod.Get,
};

export type IsUserOrParentEmailRegistered = ApiData<{ email: string }, boolean>;

export const me: ApiDescription = {
  endpoint: '/users/me',
  method: RequestsMethod.Get,
};
export type Me = ApiData<never, MyAccountInfo>;

export const updateMyMentorData: ApiDescription = {
  endpoint: '/users/me/mentor-data',
  method: RequestsMethod.Patch,
};

type StudySubjectMentor = MentorData & {
  studySubjects: string[];
  userId?: string;
};
export type UpdateMyMentorData = ApiData<Partial<StudySubjectMentor>, User>;

export const searchStudents: ApiDescription = {
  endpoint: '/users/search-students',
  method: RequestsMethod.Get,
};

export type SearchStudents = ApiData<
  {
    search?: string;
    mentoredBy?: string;
    enrolledIn?: string[];
    graduationYears?: string[];
    tiers?: StudentTier[];
    applicationStages?: GoalStatusTypes[];
    universities?: string[];
    majors?: string[];
    status?: string;
    page?: number;
    limit?: number;
    sortDirection?: 'asc' | 'desc';
    sortBy?: 'firstName' | 'lastName' | 'graduationYear';
    extraFilter?: any;
    isTestUser?: boolean;
    isDeactivated?: boolean;
    hasCoachMe?: boolean;
    hasContract?: boolean;
    contractStage?: string;
    contractProperty?: string;
    contractValue?: string;
  },
  SearchStudentsDTO
>;

export const exportStudentContacts: ApiDescription = {
  endpoint: '/users/students/contacts-export',
  method: RequestsMethod.Post,
};

export type StudentContactExportRow = {
  name: string;
  phone: string;
  email: string;
  admissions: { university: string; status: string }[];
  mentors: { name: string; phone: string; email: string }[];
  inactiveMentorCount: number;
};

export type ExportStudentContacts = ApiData<
  { studentIds: string[] },
  { rows: StudentContactExportRow[] }
>;

export const searchStudentsForMeeting: ApiDescription = {
  endpoint: '/users/search-students-for-meeting',
  method: RequestsMethod.Get,
};

export type SearchStudentsForMeeting = ApiData<{ search?: string }, SearchStudentsForMeetingDTO>;

export const updateMentorData: ApiDescription = {
  endpoint: '/users/:id/mentor-data',
  method: RequestsMethod.Patch,
};
export type UpdateMentorData = ApiData<Partial<MentorData>, MentorData>;

export const getStudentDetails: ApiDescription = {
  endpoint: '/users/student/:id',
  method: RequestsMethod.Get,
};
export type GetStudentDetails = ApiData<{ studentId: string }, User>;

export const searchUsers: ApiDescription = {
  endpoint: '/users',
  method: RequestsMethod.Get,
};
export type SearchUsers = ApiData<
  {
    page?: number;
    limit?: number;
    roles?: RoleType[];
    search?: string;
    countries?: string[];
    majors?: string[];
    teachesCourse?: string;
    courseId?: string;
    mentorId?: string;
    graduationYear?: number;
    sortBy?: 'createdAt' | 'avgSatisfaction' | 'courseStartDate';
    sortDirection?: 'asc' | 'desc';
    isTakingCourse?: boolean;
    isDeactivated?: string;
    isTestUser?: string;
  },
  SearchUsersDTO
>;

export const searchUsersV2: ApiDescription = {
  endpoint: '/users-v2',
  method: RequestsMethod.Get,
};
export type SearchUsersV2 = ApiData<
  {
    page?: number;
    limit?: number;
    roles?: RoleType[];
    search?: string;
    showTestUsers?: TriState;
    status?: string;
    sortBy?: 'createdAt' | 'firstName' | 'lastName';
    sortDirection?: 'asc' | 'desc';
  },
  SearchUsersDTO
>;

export const searchUsersByName: ApiDescription = {
  endpoint: '/users/search-by-name',
  method: RequestsMethod.Get,
};

export type SearchUsersByName = ApiData<
  {
    name?: string;
    role: RoleType;
  },
  User[]
>;

export const updateUserNote: ApiDescription = {
  endpoint: '/users/:id/update-notes',
  method: RequestsMethod.Post,
};
export type UpdateUserNote = ApiData<{ notes: string[] }, string>;

export const getUserDetails: ApiDescription = {
  endpoint: '/users/:id/details',
  method: RequestsMethod.Get,
};
export type GetUserDetails = ApiData<unknown, GetUserDetailsDTO>;

export const getUserNotes: ApiDescription = {
  endpoint: '/users/:id/notes',
  method: RequestsMethod.Get,
};
export type GetUserNotes = ApiData<unknown, Array<string>>;

export const getAllMentors: ApiDescription = {
  endpoint: '/mentor/get-all',
  method: RequestsMethod.Get,
};
export type GetAllMentors = ApiData<
  { search?: string; limit?: number; active?: boolean; haveStatusInactiveOrLeaving?: boolean },
  User[]
>;

export const getAllStudents: ApiDescription = {
  endpoint: '/student/get-all',
  method: RequestsMethod.Get,
};

export type GetAllStudents = ApiData<{ search?: string; limit?: number; active?: boolean }, User[]>;

export const getAllParents: ApiDescription = {
  endpoint: '/parents/get-all',
  method: RequestsMethod.Get,
};

export type GetAllParents = ApiData<{ search?: string; limit?: number }, User[]>;

export const getUserProfile: ApiDescription = {
  endpoint: '/users/:id/profile',
  method: RequestsMethod.Get,
};

export type GetUserProfile = ApiData<unknown, Partial<UserProfileDTO>>;

export const getMyWhatsAppGroups: ApiDescription = {
  endpoint: '/users/whatsapp-groups',
  method: RequestsMethod.Get,
};

export type GetMyWhatsAppGroups = ApiData<unknown, WhatsAppGroupsDTO>;

export const setWhatsAppGroupJoined: ApiDescription = {
  endpoint: '/users/whatsapp-groups/joined',
  method: RequestsMethod.Post,
};

export type SetWhatsAppGroupJoined = ApiData<
  { studentId: string; joined: boolean },
  { studentId: string; joined: boolean }
>;

export const dismissWhatsAppReminder: ApiDescription = {
  endpoint: '/users/whatsapp-groups/dismiss',
  method: RequestsMethod.Post,
};

export type DismissWhatsAppReminder = ApiData<unknown, { dismissedAt: string }>;

export const getStudentWhatsAppGroup: ApiDescription = {
  endpoint: '/users/student-whatsapp-group',
  method: RequestsMethod.Get,
};

export type GetStudentWhatsAppGroup = ApiData<unknown, WhatsAppGroupsDTO>;

export const setStudentWhatsAppGroupJoined: ApiDescription = {
  endpoint: '/users/student-whatsapp-group/joined',
  method: RequestsMethod.Post,
};

export type SetStudentWhatsAppGroupJoined = ApiData<{ joined: boolean }, { joined: boolean }>;

export const dismissStudentWhatsAppReminder: ApiDescription = {
  endpoint: '/users/student-whatsapp-group/dismiss',
  method: RequestsMethod.Post,
};

export type DismissStudentWhatsAppReminder = ApiData<unknown, { dismissedAt: string }>;

export const addUserComment: ApiDescription = {
  endpoint: '/users/:id/comments',
  method: RequestsMethod.Post,
};

export type AddUserComment = ApiData<{ text: string }, UserCommentDTO[]>;

export const saveUserProfile: ApiDescription = {
  endpoint: '/users/:id/save-profile',
  method: RequestsMethod.Put,
};

export type SaveUserProfile = ApiData<FormData | any, Partial<User>>;

export const uploadMentorCv: ApiDescription = {
  endpoint: '/users/me/mentor-cv',
  method: RequestsMethod.Post,
};

export type UploadMentorCv = ApiData<FormData, { cv: string }>;

export const downloadMentorCv: ApiDescription = {
  endpoint: '/users/:id/mentor-cv',
  method: RequestsMethod.Get,
};

export type DownloadMentorCv = ApiData<unknown, Blob>;

export const getUserEnrollmentTimeline: ApiDescription = {
  endpoint: '/users/:id/enrollment-timeline',
  method: RequestsMethod.Get,
};

export type GetUserEnrollmentTimeline = ApiData<unknown, GetParentViewEnrollmentTimelineDTO>;

export const getUserUniversitiesApplyingTo: ApiDescription = {
  endpoint: '/users/:id/universities-applying-to',
  method: RequestsMethod.Get,
};

export type GetUserUniversitiesApplyingTo = ApiData<
  unknown,
  GetParentViewUniversitiesApplyingToDTO
>;

export const updateMentorComment: ApiDescription = {
  endpoint: '/users/:id/mentor-comment',
  method: RequestsMethod.Put,
};

export type UpdateMentorComment = ApiData<
  { comment: string },
  { adminComment?: MentorAdminComment }
>;

export const getStudentStatistics: ApiDescription = {
  endpoint: '/users/students/statistics',
  method: RequestsMethod.Get,
};

export type GetStudentStatistics = ApiData<unknown, any>;

export const editUserPermissions: ApiDescription = {
  endpoint: '/users/:id/permissions',
  method: RequestsMethod.Patch,
};

export type EditUserPermissions = ApiData<{ permissions: PermissionsType }, unknown>;

export const getUserPermissions: ApiDescription = {
  endpoint: '/users/:id/permissions',
  method: RequestsMethod.Get,
};

export type GetUserPermissions = ApiData<unknown, PermissionsType>;

export const migratePayrateFromHubspot: ApiDescription = {
  endpoint: '/users/migrate-payrate-from-hubspot',
  method: RequestsMethod.Post,
};

export type MigratePayrateFromHubspot = ApiData<unknown, unknown>;

export type DeleteVirtualBackground = ApiData<{ background: string }, string>;

export const deleteVirtualBackground: ApiDescription = {
  endpoint: '/users/me/virtual-backgrounds',
  method: RequestsMethod.Put,
};

export const addVirtualBackground: ApiDescription = {
  endpoint: '/users/me/virtual-backgrounds',
  method: RequestsMethod.Patch,
};

export type AddVirtualBackground = ApiData<FormData, string>;

export const getVirtualBackgrounds: ApiDescription = {
  endpoint: '/users/me/virtual-backgrounds',
  method: RequestsMethod.Get,
};

export type GetVirtualBackgrounds = ApiData<unknown, string[]>;

export const getVideoSettings: ApiDescription = {
  endpoint: '/users/me/video-settings',
  method: RequestsMethod.Get,
};

export type GetVideoSettings = ApiData<unknown, GetVideoSettingsDTO>;

export const updateVideoSettings: ApiDescription = {
  endpoint: '/users/me/video-settings',
  method: RequestsMethod.Patch,
};

export type UpdateVideoSettings = ApiData<
  {
    noiseSuppression?: boolean;
    virtualBackground?: string | null;
  },
  unknown
>;

export const getTimezonesList: ApiDescription = {
  endpoint: '/users/me/timezones-list',
  method: RequestsMethod.Get,
};

export type GetTimezonesList = ApiData<unknown, string[]>;

export const deleteAdmissionDocument: ApiDescription = {
  endpoint: '/users/:id/delete-admission-document',
  method: RequestsMethod.Put,
};

export type DeleteAdmissionDocument = ApiData<
  { appliedUniversity: string; admissionDocument: string },
  DeleteAdmissionDocumentDTO
>;

export const getMentorStatisticsByMentorStatus: ApiDescription = {
  endpoint: '/users/stats/mentor-statuses',
  method: RequestsMethod.Get,
};
export type GetMentorStatisticsByMentorStatus = ApiData<never, StatsByMetricDTO>;

export const getMentorsByStatus: ApiDescription = {
  endpoint: '/users/stats/mentors-by-status',
  method: RequestsMethod.Get,
};
export type GetMentorsByStatus = ApiData<
  PaginationAndSortingParams & { status: string },
  GetUsersByStatsMetricDTO
>;

export const getMentorStatisticsByCoursesCanTeach: ApiDescription = {
  endpoint: '/users/stats/mentor-courses-can-teach',
  method: RequestsMethod.Get,
};
export type GetMentorStatisticsByCoursesCanTeach = ApiData<never, StatsByMetricDTO>;

export const getMentorsByCoursesCanTeach: ApiDescription = {
  endpoint: '/users/stats/mentors-by-courses-can-teach',
  method: RequestsMethod.Get,
};
export type GetMentorsByCoursesCanTeach = ApiData<
  PaginationAndSortingParams & { courseCanTeach: string },
  GetUsersByStatsMetricDTO
>;

export const getUserStatisticsByProfileCompletion: ApiDescription = {
  endpoint: '/users/stats/user-profile-completion',
  method: RequestsMethod.Get,
};
export type GetUserStatisticsByProfileCompletion = ApiData<
  { role: UserRole; fields: ProfileCompletionFieldsType[] },
  StatsByMetricDTO
>;

export const getUsersByProfileCompletion: ApiDescription = {
  endpoint: '/users/stats/users-by-profile-completion',
  method: RequestsMethod.Get,
};
export type GetUsersByProfileCompletion = ApiData<
  PaginationAndSortingParams & {
    profileMetric: string;
    role: UserRole;
    fields: ProfileCompletionFieldsType[];
  },
  GetUsersByStatsMetricDTO
>;

export const getMentorStatisticsByUniversity: ApiDescription = {
  endpoint: '/users/stats/mentor-universities',
  method: RequestsMethod.Get,
};
export type GetMentorStatisticsByUniversity = ApiData<never, StatsByMetricDTO>;

export const getMentorsByUniversity: ApiDescription = {
  endpoint: '/users/stats/mentors-by-university',
  method: RequestsMethod.Get,
};
export type GetMentorsByUniversity = ApiData<
  PaginationAndSortingParams & { university: string },
  GetUsersByStatsMetricDTO
>;

export const getMentorStatisticsByUniversityRegion: ApiDescription = {
  endpoint: '/users/stats/mentor-universities-region',
  method: RequestsMethod.Get,
};
export type GetMentorStatisticsByUniversityRegion = ApiData<never, StatsByMetricDTO>;

export const getMentorsByUniversityRegion: ApiDescription = {
  endpoint: '/users/stats/mentors-by-university-region',
  method: RequestsMethod.Get,
};
export type GetMentorsByUniversityRegion = ApiData<
  PaginationAndSortingParams & { region: string },
  GetUsersByStatsMetricDTO
>;

export const getUserStatisticsByCountry: ApiDescription = {
  endpoint: '/users/stats/user-countries',
  method: RequestsMethod.Get,
};
export type GetUserStatisticsByCountry = ApiData<
  { role: UserRole; param: UserFieldParam.Country },
  StatsByMetricDTO
>;
export const getUsersByCountry: ApiDescription = {
  endpoint: '/users/stats/users-by-countries',
  method: RequestsMethod.Get,
};
export type GetUsersByCountry = ApiData<
  PaginationAndSortingParams & {
    role: UserRole;
    country: string;
    param: UserFieldParam.Country;
  },
  GetUsersByStatsMetricDTO
>;

export const getUserStatisticsByCity: ApiDescription = {
  endpoint: '/users/stats/user-cities',
  method: RequestsMethod.Get,
};
export type GetUserStatisticsByCity = ApiData<
  { role: UserRole; param: UserFieldParam.City },
  StatsByMetricDTO
>;
export const getUsersByCity: ApiDescription = {
  endpoint: '/users/stats/users-by-cities',
  method: RequestsMethod.Get,
};
export type GetUsersByCity = ApiData<
  PaginationAndSortingParams & {
    role: UserRole;
    city: string;
    param: UserFieldParam.City;
  },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByUniversity: ApiDescription = {
  endpoint: '/users/stats/student-universities',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByUniversity = ApiData<never, StatsByMetricDTO>;

export const getStudentsByUniversity: ApiDescription = {
  endpoint: '/users/stats/students-by-university',
  method: RequestsMethod.Get,
};
export type GetStudentsByUniversity = ApiData<
  PaginationAndSortingParams & { university: string },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByUniversityOffersAccepted: ApiDescription = {
  endpoint: '/users/stats/student-universities-offers-accepted',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByUniversityOffersAccepted = ApiData<never, StatsByMetricDTO>;

export const getStudentsByUniversityOffersAccepted: ApiDescription = {
  endpoint: '/users/stats/students-by-university-offers-accepted',
  method: RequestsMethod.Get,
};
export type GetStudentsByUniversityOffersAccepted = ApiData<
  PaginationAndSortingParams & { university: string },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByUniversityApplicationsOpened: ApiDescription = {
  endpoint: '/users/stats/student-universities-applications-opened',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByUniversityApplicationsOpened = ApiData<never, StatsByMetricDTO>;

export const getStudentsByUniversityApplicationsOpened: ApiDescription = {
  endpoint: '/users/stats/students-by-university-applications-opened',
  method: RequestsMethod.Get,
};
export type GetStudentsByUniversityApplicationsOpened = ApiData<
  PaginationAndSortingParams & { university: string },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByUniversityApplicationsOpenedRegion: ApiDescription = {
  endpoint: '/users/stats/student-universities-applications-opened-region',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByUniversityApplicationsOpenedRegion = ApiData<
  never,
  StatsByMetricDTO
>;

export const getStudentsByUniversityApplicationsOpenedRegion: ApiDescription = {
  endpoint: '/users/stats/students-by-university-applications-opened-region',
  method: RequestsMethod.Get,
};
export type GetStudentsByUniversityApplicationsOpenedRegion = ApiData<
  PaginationAndSortingParams & { region: string },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByUniversityApplicationsOpenedCountry: ApiDescription = {
  endpoint: '/users/stats/student-universities-applications-opened-country',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByUniversityApplicationsOpenedCountry = ApiData<
  never,
  StatsByMetricDTO
>;

export const getStudentsByUniversityApplicationsOpenedCountry: ApiDescription = {
  endpoint: '/users/stats/students-by-university-applications-opened-country',
  method: RequestsMethod.Get,
};
export type GetStudentsByUniversityApplicationsOpenedCountry = ApiData<
  PaginationAndSortingParams & { country: string },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByUniversityAdmissionRate: ApiDescription = {
  endpoint: '/users/stats/student-universities-admission-rate',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByUniversityAdmissionRate = ApiData<never, StatsByMetricDTO>;

export const getStudentsByUniversityAdmissionRate: ApiDescription = {
  endpoint: '/users/stats/students-by-university-admission-rate',
  method: RequestsMethod.Get,
};
export type GetStudentsByUniversityAdmissionRate = ApiData<
  PaginationAndSortingParams & { university: string },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByApplicationStatus: ApiDescription = {
  endpoint: '/users/stats/students-application-statuses',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByApplicationStatus = ApiData<
  Record<string, never>,
  StatsByMetricDTO
>;

export const getStudentsByApplicationStatus: ApiDescription = {
  endpoint: '/users/stats/students-by-application-status',
  method: RequestsMethod.Get,
};
export type GetStudentsByApplicationStatus = ApiData<
  PaginationAndSortingParams & { status: number },
  GetUsersByStatsMetricDTO
>;

export const getStudentStatisticsByEnrollments: ApiDescription = {
  endpoint: '/users/stats/students-enrollments',
  method: RequestsMethod.Get,
};
export type GetStudentStatisticsByEnrollments = ApiData<{ isActive: boolean }, StatsByMetricDTO>;

export const getStudentsByEnrollments: ApiDescription = {
  endpoint: '/users/stats/students-by-enrollments',
  method: RequestsMethod.Get,
};
export type GetStudentsByEnrollments = ApiData<
  PaginationAndSortingParams & { courseTitle: string; isActive: boolean },
  GetUsersByStatsMetricDTO
>;

export const downloadStudentApplicationsCSV: ApiDescription = {
  endpoint: '/users/download-student-applications-csv',
  method: RequestsMethod.Get,
};
export type DownloadStudentApplicationsCSV = ApiData<never, DownloadStudentApplicationsCSVDTO>;

export const exportRole4UsersCSV: ApiDescription = {
  endpoint: '/users/export-csv',
  method: RequestsMethod.Get,
};
export type ExportRole4UsersCSV = ApiData<
  { startDate: string; endDate: string },
  ExportRole4UsersCSVDTO
>;

export const getStudentStatisticsByIntendedMajors: ApiDescription = {
  endpoint: '/users/stats/student-intended-majors',
  method: RequestsMethod.Get,
};

export type GetStudentStatisticsByIntendedMajors = ApiData<Record<string, never>, StatsByMetricDTO>;

export const getStudentsByIntendedMajor: ApiDescription = {
  endpoint: '/users/stats/students-by-intended-major',
  method: RequestsMethod.Get,
};

export type GetStudentsByIntendedMajor = ApiData<
  PaginationAndSortingParams & { major: string; sortBy?: string; sortDirection?: 'asc' | 'desc' },
  GetUsersByStatsMetricDTO
>;
